'use client';

import React from 'react';

import { Typography } from '@/components/atoms';
import { CTAPreviewBanner } from '@/components/molecules/CTAPreviewBanner';
import {
  ContentBanner,
  WidgetRenderer,
  TextBanner,
} from '@/components/organisms';
import { useAuthContext } from '@/lib/contexts/auth';
import { useSiteBootstrap } from '@/lib/contexts/SiteBootstrapProvider';
import { usePageDetails, useSitePages } from '@/lib/hooks/query';
import { ScrollToTop } from '@/lib/utils/scrollToTop';

import type { AccessScope } from '@/lib/auth';

import type { Header, Page } from '@/lib/api/types';

type HomeClientProps = {
  initialSlug?: string[];
};

export const HomeClient = ({ initialSlug }: HomeClientProps) => {
  const { isAuthenticated } = useAuthContext();
  const scope: AccessScope = isAuthenticated ? 'private' : 'public';

  const { siteConfig: site } = useSiteBootstrap();

  const { pages } = useSitePages(site.id, site.configuration.tenant_id, scope);

  const slug = initialSlug?.[0];
  const currentPage = pages?.find(p => (slug ? p.slug === slug : p.is_home));

  const pageDetailsQuery = usePageDetails(
    site?.id,
    currentPage?.id,
    site?.configuration?.tenant_id,
    scope,
  );

  const pageDetails = pageDetailsQuery.data;

  const renderHeader = (header?: Header) => {
    if (!header || !header.displayed) return null;

    if (header.type === 'text') {
      return (
        <TextBanner
          bgColor={header.configuration?.background_color ?? ''}
          title={header.call_to_action.title}
          subTitle={header.call_to_action.subtitle}
        />
      );
    }

    if (header.type === 'image') {
      return (
        <ContentBanner
          title={header.call_to_action.title}
          subtitle={header.call_to_action.subtitle}
          media={header.configuration?.image}
          buttonLabel={header.call_to_action?.cta_text ?? ''}
          {...(header.linked_content !== undefined
            ? { content: header.linked_content }
            : {})}
        />
      );
    }

    if (header.type === 'video') {
      const { call_to_action, configuration } = header;

      if (configuration.preview_clips === undefined) {
        return;
      }

      return (
        <CTAPreviewBanner
          previewClips={configuration.preview_clips}
          primaryButtonText={call_to_action.cta_text}
          title={call_to_action.title}
          description={call_to_action.subtitle}
          {...(header.linked_content !== undefined
            ? { linkedContent: header.linked_content }
            : {})}
          loop={configuration.loop ?? false}
        />
      );
    }

    // Unknown header type: render nothing (avoid throwing in render)
    return null;
  };

  const siteData = {
    site,
    pages: pages as Page[],
  };

  const header = renderHeader(pageDetails?.header);
  const hasHeaderTitle =
    Boolean(header) && Boolean(pageDetails?.header?.call_to_action?.title);
  const fallbackTitle = currentPage?.is_home ? site.name : currentPage?.name;

  return (
    <>
      {header}

      <main
        className="
          flex min-h-screen flex-col items-center
          w-full px-(--horizontal-spacing-4) gap-(--layout-widget-gap)
          pt-(--vertical-spacing-8) pb-(--vertical-spacing-0)
          min-[820px]:max-w-[820px] min-[820px]:mx-auto
          lg:max-w-(--layout-dynamic-max-width) lg:mx-auto
        "
      >
        <ScrollToTop />
        {!hasHeaderTitle && fallbackTitle && (
          <Typography.Heading className="sr-only">
            {fallbackTitle}
          </Typography.Heading>
        )}

        {pageDetails?.widgets?.map((widget, idx) => (
          <WidgetRenderer
            key={`${widget.id}-${idx}`}
            widget={widget}
            siteData={siteData}
          />
        ))}
      </main>
    </>
  );
};
